import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Maximize2, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { viewerApi } from "../../api/viewer";
import { AskAiPanel } from "../../components/AskAiPanel";
import { Badge, Button, Card, EmptyState, formatRelative } from "../../ds";
import { usePageTitle } from "../../shell/AppShell";
import { PagePicker } from "../../shell/PagePicker";
import type { TvFramePageInfo } from "../../shell/TvFrame";
import { FitModeToggle, TvFrameViewer, type TvFrameViewerHandle, type ViewerFitMode } from "../../shell/TvFrameViewer";
import { PreviewThemeToggle, useViewerTheme } from "../../shell/TvFrameTheme";

/** A published report exactly as the office TVs show it (same renderer),
 * with page navigation and a real full-screen mode for meeting rooms.
 *
 * Same stage as the share link and the studio's published preview: the page's
 * own canvas (any size, e.g. A4) uniformly scaled; full screen shows only the
 * report scaled to the screen. `?theme=light|dark` picks the colour mode. */
export function ReportViewerPage() {
  const { reportId = "" } = useParams();
  const navigate = useNavigate();
  const published = useQuery({ queryKey: ["published", reportId], queryFn: () => viewerApi.getPublished(reportId), retry: false });
  const [pages, setPages] = useState<TvFramePageInfo[]>([]);
  const [pageId, setPageId] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [fitMode, setFitMode] = useState<ViewerFitMode>("page");
  const viewer = useRef<TvFrameViewerHandle>(null);
  const definition = published.data?.definition as { name?: string; settings?: { defaultTheme?: unknown } } | undefined;
  const [theme, setTheme] = useViewerTheme(definition?.settings?.defaultTheme);
  const name = definition?.name;
  const [filters, setFilters] = useState<unknown[]>([]);
  useEffect(() => {
    const h = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "mrp:filters" && Array.isArray(e.data.filters)) setFilters(e.data.filters);
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, []);
  usePageTitle(name);

  if (published.isError) {
    return (
      <div className="ds-page">
        <Card>
          <EmptyState icon={AlertTriangle} title="This report isn't available" description="It may not be published yet, or you don't have access to it." action={<Button onClick={() => navigate("/viewer/reports")}>Back to reports</Button>} />
        </Card>
      </div>
    );
  }

  return (
    <div className="ds-page ds-page--wide" style={{ maxWidth: 1760 }}>
      <div className="ds-row" style={{ marginBottom: 12, flexWrap: "wrap" }}>
        <div className="ds-grow" style={{ minWidth: 0 }}>
          <h1 className="ds-page-title ds-truncate" style={{ fontSize: 22 }}>{name ?? "…"}</h1>
          {published.data ? <div className="ds-small ds-muted">Version {published.data.version} · published {formatRelative(published.data.publishedAt)} · <Badge tone="success" dot>Live data</Badge></div> : null}
        </div>
        <FitModeToggle value={fitMode} onChange={setFitMode} />
        <PreviewThemeToggle theme={theme} onChange={setTheme} label="Screen" />
        <Button icon={RefreshCw} onClick={() => setReload((r) => r + 1)}>Refresh</Button>
        <Button variant="primary" icon={Maximize2} onClick={() => viewer.current?.toggleFullscreen()}>Full screen</Button>
      </div>
      <div style={{ marginBottom: 12 }}><PagePicker pages={pages} value={pageId} onChange={(id) => { setPageId(id); viewer.current?.frame?.post({ type: "mrp:selectPage", pageId: id }); }} /></div>
      <TvFrameViewer
        ref={viewer}
        reportId={reportId}
        source="published"
        theme={theme}
        onThemeChange={setTheme}
        fitMode={fitMode}
        onFitMode={setFitMode}
        pages={pages}
        pageId={pageId}
        reserve={230}
        reloadKey={reload}
        onLoaded={(ps, id) => { setPages(ps); setPageId(id); }}
        onPage={setPageId}
      />
      {published.data ? <AskAiPanel reportId={reportId} pageId={pageId} filters={filters} /> : null}
    </div>
  );
}
